import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, AlertCircle, MapPin, Phone, MessageCircle, Quote } from "lucide-react";
import Seo, { personSchema, breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal, SectionHeading } from "@/components/public/Primitives";
import { HOODI_MISSION } from "@/data/constants";
import { useLanguage } from "@/context/LanguageContext";
import { getSocialWork, getSettings } from "@/lib/api";

const KEY_ISSUES = [
  "Road and footpath repairs across Hoodi neighbourhoods",
  "Monsoon waterlogging and drainage bottlenecks",
  "Irregular garbage collection and cleanliness",
  "Streetlight gaps and public safety concerns",
  "Drinking water supply and public utilities",
  "Maintenance of parks and shared public spaces",
];

export default function HoodiWard29() {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const { data: posts = [] } = useQuery({ queryKey: ["social-work-home"], queryFn: () => getSocialWork() });
  const { tr, lang } = useLanguage();
  const wa = (settings?.whatsapp || "").replace(/[^\d]/g, "");

  return (
    <div data-testid="hoodi-ward-29-page">
      <PageSeo
        slug="hoodi-ward-29"
        title="Girish Kumar Naidu | GBA Hoodi Ward 29 Aspirant Candidate"
        description="Official public-service website of Girish Kumar Naidu, aspirant candidate for GBA Hoodi Ward 29, focused on social service, ward development, citizen issues, and civic leadership."
        keywords="Girish Kumar Naidu Hoodi, Girish Kumar Naidu Ward 29, Girish Kumar Naidu GBA Hoodi Ward 29, Hoodi Ward 29 aspirant candidate, GBA Hoodi Ward 29, Hoodi ward development, Hoodi civic issues, Hoodi social worker, Hoodi public service, Greater Bengaluru Authority Hoodi Ward"
        schemas={[
          personSchema(settings),
          breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Hoodi Ward 29" }]),
        ]}
      />
      <PageHeader
        overline={tr("page.hoodi.overline")}
        title={tr("page.hoodi.title")}
        subtitle={tr("page.hoodi.subtitle")}
        crumbs={[{ name: "Hoodi Ward 29" }]}
      />

      {/* About focus + map */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-gold font-mono text-[10px] font-bold uppercase tracking-[0.2em] mb-6">
              <MapPin className="h-3.5 w-3.5" /> Hoodi Ward 29 · Greater Bengaluru Authority
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-black text-navy tracking-tight">About the Hoodi Ward 29 Focus</h2>
            <p className="mt-5 text-gray-600 leading-relaxed">
              Girish Kumar Naidu's current public-service focus is Hoodi Ward 29 under the Greater Bengaluru Authority.
              His approach is grassroots and simple: be accessible to residents, listen to local concerns, document
              issues clearly, and follow up with the concerned authorities until there is practical progress.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              This is a service-first commitment to Hoodi Ward 29 residents — built on years of charitable and
              community work across Bengaluru.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl overflow-hidden border border-gray-200 shadow-xl">
              <div className="bg-navy px-5 py-3 flex items-center gap-2 text-white">
                <MapPin className="h-4 w-4 text-gold" /> <span className="font-mono text-xs uppercase tracking-wider">Hoodi Ward 29, Bengaluru</span>
              </div>
              <iframe title="Hoodi Ward 29 map" src="https://www.google.com/maps?q=Hoodi,Bengaluru&output=embed" className="w-full h-72 border-0" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Key civic issues */}
      <section className="bg-paper py-16 md:py-24 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading overline="Key Civic Issues" title="What Hoodi Ward 29 Residents Face" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {KEY_ISSUES.map((issue, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div className="flex items-start gap-3 bg-white rounded-2xl p-6 border border-gray-200 card-lift h-full">
                  <AlertCircle className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                  <p className="text-gray-700 font-medium">{issue}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Development priorities */}
      <section className="bg-navy py-16 md:py-24 grain">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading light center overline="Development Priorities" title="A Practical Mission for Hoodi Ward 29" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HOODI_MISSION.map((m, i) => (
              <Reveal key={m.title} delay={(i % 3) * 0.05}>
                <div className="glass-dark rounded-2xl p-6 flex items-start gap-4 hover:border-gold/40 transition-colors h-full">
                  <div className="h-11 w-11 rounded-xl bg-gold flex items-center justify-center shrink-0">
                    <m.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-white leading-snug">{(lang === "kn" && m.title_kn) || m.title}</h3>
                    <p className="text-sm text-slate-300 mt-1 leading-relaxed">{(lang === "kn" && m.desc_kn) || m.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Issue submission CTA */}
      <section className="bg-gold py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <AlertCircle className="h-12 w-12 text-white mx-auto mb-5" />
          <h2 className="font-display text-3xl md:text-5xl font-black text-white tracking-tight">Have an Issue in Hoodi Ward 29?</h2>
          <p className="mt-4 text-white/90 text-lg font-medium">Report it directly to Girish Kumar Naidu's public service team — and track it until it's resolved.</p>
          <Link to="/submit-ward-issue" data-testid="hoodi-submit-cta" className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full bg-navy text-white font-bold hover:bg-navy-800 transition-colors">
            Submit Hoodi Ward 29 Issue <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Social work near Hoodi */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <SectionHeading overline="Social Work" title="Service in and around Hoodi" />
            <Link to="/social-work" className="inline-flex items-center gap-2 text-navy font-bold hover:text-gold transition-colors mb-12 md:mb-16">View all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link to={`/social-work/${p.slug}`} className="group block bg-white rounded-2xl overflow-hidden border border-gray-200 card-lift h-full">
                  <div className="h-48 overflow-hidden">
                    <img src={p.featuredImage} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-civic">{p.category}</span>
                    <h3 className="font-display text-lg font-bold text-navy mt-1 group-hover:text-gold transition-colors line-clamp-2">{p.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment + contact */}
      <section className="bg-navy py-16 md:py-24 grain">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="glass-dark rounded-3xl p-8 md:p-12 text-center">
            <Quote className="h-8 w-8 text-gold mx-auto mb-5" />
            <p className="font-display text-xl md:text-2xl text-white leading-relaxed font-medium">
              "My commitment to Hoodi Ward 29 is simple — be present, listen to residents, and follow up on every issue
              with honesty and accountability. People-first, always."
            </p>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mt-6">— Girish Kumar Naidu</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-civic text-white font-bold hover:bg-civic-dark transition-colors">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
              <a href={`tel:${settings?.phone || ""}`} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gold text-white font-bold btn-saffron">
                <Phone className="h-4 w-4" /> Contact Office
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
